/**
 * useClipManagement.ts
 * 
 * Manages the core timeline state: clips, tracks, history, selection, and navigation.
 * Uses recursive helpers for handling nested compositions (Synthé/Panel).
 */

import React, { useState, useMemo, useCallback, useRef } from 'react';
import { Clip, Track } from '../types';
import { updateClipDeep } from '../utils/clip-helpers';

/**
 * Initial track configuration
 */
const INITIAL_TRACKS: Track[] = [
    { id: 't1', label: 'Overlays', accepts: ['text', 'image', 'synthe', 'panel'], isMagnetic: false, isVisible: true, isMuted: true },
    { id: 's1', label: 'Split', accepts: ['video', 'image', 'text'], isMagnetic: false, isVisible: true, isMuted: false },
    { id: 'v1', label: '1', accepts: ['video', 'image', 'audio', 'text'], isMagnetic: false, isVisible: true, isMuted: false },
    { id: 'v2', label: '2', accepts: ['video', 'image', 'audio', 'text'], isMagnetic: false, isVisible: true, isMuted: false },
    { id: 'z1', label: 'Zoom', accepts: ['zoom'], isMagnetic: false, isVisible: true, isMuted: true },
];

export const useClipManagement = () => {
    // ============================================================================
    // CORE STATE
    // ============================================================================
    const [allClips, setAllClips] = useState<Clip[]>([]);
    const [tracks, setTracks] = useState<Track[]>(INITIAL_TRACKS); // Tracks can be dynamic based on scope? For now mostly global.

    // Selection
    const [selectedClipId, setSelectedClipId] = useState<string | null>(null);
    const [isClipContextMenuOpen, setIsClipContextMenuOpen] = useState(false);

    // History (Undo/Redo)
    const [history, setHistory] = useState<Clip[][]>([[]]);
    const [historyIndex, setHistoryIndex] = useState(0);

    // Navigation (Synthé/Panel recursive editing)
    const [navigationStack, setNavigationStack] = useState<Clip[]>([]);

    // Cache for source clips (optimisation for reinstating originals)
    const [cachedSourceClips, setCachedSourceClips] = useState<Clip[]>([]);

    // ============================================================================
    // CURRENT SCOPE (Derived State)
    // ============================================================================

    // Determines which clips are currently visible/editable based on navigation depth
    const currentScope = useMemo(() => {
        if (navigationStack.length === 0) {
            return { clips: allClips, tracks };
        }

        // Traverse allClips using the IDs in navigationStack to get the fresh container
        let searchClips = allClips;
        let currentContainer: Clip | undefined;

        for (const stackItem of navigationStack) {
            currentContainer = searchClips.find(c => c.id === stackItem.id);
            if (!currentContainer || !currentContainer.syntheData) {
                return { clips: [], tracks: INITIAL_TRACKS };
            }
            searchClips = currentContainer.syntheData.clips;
        }

        return {
            clips: currentContainer?.syntheData?.clips || [],
            tracks: currentContainer?.syntheData?.tracks || INITIAL_TRACKS
        };
    }, [navigationStack, allClips, tracks]);

    // ============================================================================
    // HISTORY MANAGEMENT
    // ============================================================================

    const saveStateToHistory = useCallback((newState: Clip[]) => {
        // Dedup: don't save if identical to current
        const newHistory = history.slice(0, historyIndex + 1);
        // Simple JSON comparison for now (ok for this data size)
        if (newHistory.length > 0 && JSON.stringify(newHistory[newHistory.length - 1]) === JSON.stringify(newState)) return;

        newHistory.push(newState);
        setHistory(newHistory);
        setHistoryIndex(newHistory.length - 1);
    }, [history, historyIndex]);

    const undo = useCallback(() => {
        if (historyIndex > 0) {
            const newIndex = historyIndex - 1;
            setAllClips(history[newIndex]);
            setHistoryIndex(newIndex);
        }
    }, [history, historyIndex]);

    const redo = useCallback(() => {
        if (historyIndex < history.length - 1) {
            const newIndex = historyIndex + 1;
            setAllClips(history[newIndex]);
            setHistoryIndex(newIndex);
        }
    }, [history, historyIndex]);

    // ============================================================================
    // CLIP UPDATES
    // ============================================================================

    /**
     * Universal update function for clips.
     * Handles recursive updates if we are in a nested scope (navigationStack).
     * Also handles Outro positioning and Caching.
     */
    const updateClips = useCallback((
        updaterOrValue: React.SetStateAction<Clip[]>,
        saveToHistory: boolean = true
    ) => {
        setAllClips(prevClips => {
            let newClips: Clip[];

            // Resolve the new value if it's a function
            // NOTE: This logic is tricky because updater expects the CURRENT scope's clips,
            // but we need to update the ROOT allClips.

            if (navigationStack.length === 0) {
                // Simple case: Root scope
                newClips = typeof updaterOrValue === 'function'
                    ? (updaterOrValue as (prev: Clip[]) => Clip[])(prevClips)
                    : updaterOrValue;
            } else {
                // Nested case: We need to apply the update DEEP inside the tree
                // 1. Identify where we are
                const targetContainerId = navigationStack[navigationStack.length - 1].id;

                // 2. We use updateClipDeep to reach the container and update its syntheData.clips
                newClips = updateClipDeep(prevClips, targetContainerId, (container) => {
                    if (!container.syntheData) return container;

                    const currentNestedClips = container.syntheData.clips;
                    const updatedNestedClips = typeof updaterOrValue === 'function'
                        ? (updaterOrValue as (prev: Clip[]) => Clip[])(currentNestedClips)
                        : updaterOrValue;

                    return {
                        ...container,
                        syntheData: {
                            ...container.syntheData,
                            clips: updatedNestedClips
                        }
                    };
                });
            }

            // --- POST-UPDATE LOGIC ---

            // 1. Auto-position 'outro' clips (Enforce rules)
            const outroClips = newClips.filter(c => c.clipRole === 'outro');
            if (outroClips.length > 0) {
                const nonOutroClips = newClips.filter(c => c.clipRole !== 'outro');
                // Find end of timeline excluding outros
                const mainTimelineEnd = nonOutroClips.length > 0
                    ? Math.max(0, ...nonOutroClips.map(c => c.start + c.duration))
                    : 0;

                // Outros are placed 1.5s before end (transition overlap)
                const desiredStartTime = Math.max(0, mainTimelineEnd - 1.5);

                // Apply fix only if needed to avoid infinite loops
                const needsFix = outroClips.some(c => Math.abs(c.start - desiredStartTime) > 0.01);

                if (needsFix) {
                    newClips = newClips.map(clip => {
                        if (clip.clipRole === 'outro') {
                            return { ...clip, start: desiredStartTime };
                        }
                        return clip;
                    });
                }
            }

            // 2. Update Cache
            setCachedSourceClips(prevCache => {
                const cacheMap = new Map<string, Clip>(prevCache.map(c => [c.id, c]));
                // Flatten clips (naive approach, ideally we traverse)
                // For simplicity we cache only root/current scope modified clips
                // A full deep traversal for cache would be expensive.
                // Let's stick to catching clips that are being modified.

                const clipsToCache = navigationStack.length === 0 ? newClips : currentScope.clips; // Approximation

                clipsToCache.forEach(clip => {
                    if (['video', 'audio', 'image'].includes(clip.type)) {
                        const existing = cacheMap.get(clip.id);
                        const merged: Clip = existing ? Object.assign({}, existing, clip) : clip;
                        cacheMap.set(clip.id, merged);
                    }
                });
                return Array.from(cacheMap.values());
            });

            // 3. Save to History
            if (saveToHistory) {
                // Side effect inside setState is risky but common pattern for history.
                // Better to do it in useEffect, but we need the calculated newClips.
                // We'll call the callback here.
                setTimeout(() => saveStateToHistory(newClips), 0);
            }

            return newClips;
        });
    }, [navigationStack, currentScope, saveStateToHistory]);

    const handleClipSelect = useCallback((clip: Clip | null) => {
        if (!clip) {
            setSelectedClipId(null);
            setIsClipContextMenuOpen(false);
            return;
        }

        setSelectedClipId(clip.id);

        // const isAudioClip = clip.type === 'audio' || (clip.type === 'video' && !clip.url?.toLowerCase().endsWith('.gif'));
        // setIsClipContextMenuOpen(isAudioClip);
        setIsClipContextMenuOpen(false);
    }, []);

    return {
        allClips,
        setAllClips,
        tracks,
        setTracks,
        selectedClipId,
        setSelectedClipId,
        isClipContextMenuOpen,
        setIsClipContextMenuOpen,
        handleClipSelect,
        history,
        historyIndex,
        undo,
        redo,
        navigationStack,
        setNavigationStack,
        currentScope,
        updateClips,
        cachedSourceClips,
        setCachedSourceClips,
        INITIAL_TRACKS
    };
};
